export const LIST_SITES = /* GraphQL */ `
  query ListSites($limit: Int, $nextToken: String, $includeTrash: Boolean, $teamId: ID) {
    listSites(limit: $limit, nextToken: $nextToken, includeTrash: $includeTrash, teamId: $teamId) {
      items {
        siteId
        userId
        name
        url
        description
        status
        trashedAt
        createdAt
        updatedAt
        lastScanId
        lastScanAt
        lastScanScore
        lastScanGrade
        scanCount
      }
      nextToken
    }
  }
`;

export const GET_SITE = /* GraphQL */ `
  query GetSite($siteId: ID!) {
    getSite(siteId: $siteId) {
      siteId
      userId
      name
      url
      description
      status
      trashedAt
      createdAt
      updatedAt
      lastScanId
      lastScanAt
      lastScanScore
      lastScanGrade
      scanCount
      maxPages
      pauseAfterCrawl
      defaultScanTools
      thresholdFilters {
        minSeverity
        minIssueCount
        minScore
        hideAcceptedRisks
      }
      emailSettings {
        enabled
        recipients
        sendCondition
        includeChart
        senderEmail
        senderVerified
      }
      gscSettings {
        enabled
        autoSync
        lastSyncAt
        propertyUrl
        connectionStatus
      }
    }
  }
`;

export const LIST_SCANS = /* GraphQL */ `
  query ListScans($siteId: ID!, $limit: Int, $nextToken: String, $includeTrash: Boolean) {
    listScans(siteId: $siteId, limit: $limit, nextToken: $nextToken, includeTrash: $includeTrash) {
      items {
        siteId
        scanId
        userId
        status
        progress {
          phase
          pagesDiscovered
          pagesScanned
          currentPage
        }
        config {
          siteUrl
          scanTypes
          wcagVersion
          wcagLevel
          pageLimit
          rateLimit
        }
        results {
          overallScore
          overallGrade
          manualReviewCount
          totalIssues {
            critical
            serious
            moderate
            minor
            total
          }
          uniqueIssues {
            critical
            serious
            moderate
            minor
            total
          }
          categoryIssueCounts {
            accessibility {
              critical
              serious
              moderate
              minor
              total
            }
            eaa {
              critical
              serious
              moderate
              minor
              total
            }
            seo {
              critical
              serious
              moderate
              minor
              total
            }
            geo {
              critical
              serious
              moderate
              minor
              total
            }
            brokenLinks {
              critical
              serious
              moderate
              minor
              total
            }
            toxicLinks {
              critical
              serious
              moderate
              minor
              total
            }
          }
          uniqueCategoryIssueCounts {
            accessibility {
              critical
              serious
              moderate
              minor
              total
            }
            eaa {
              critical
              serious
              moderate
              minor
              total
            }
            seo {
              critical
              serious
              moderate
              minor
              total
            }
            geo {
              critical
              serious
              moderate
              minor
              total
            }
            brokenLinks {
              critical
              serious
              moderate
              minor
              total
            }
            toxicLinks {
              critical
              serious
              moderate
              minor
              total
            }
          }
        }
        startedAt
        completedAt
        trashedAt
        pdfReportUrl
        error
      }
      nextToken
    }
  }
`;

export const GET_SCAN = /* GraphQL */ `
  query GetScan($siteId: ID!, $scanId: ID!) {
    getScan(siteId: $siteId, scanId: $scanId) {
      siteId
      scanId
      userId
      status
      progress {
        phase
        pagesDiscovered
        pagesScanned
        currentPage
        estimatedTimeRemaining
        currentTool
        discoveredIssues
        stepDescription
      }
      config {
        siteUrl
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      results {
        overallScore
        overallGrade
        manualReviewCount
        totalIssues {
          critical
          serious
          moderate
          minor
          total
        }
        uniqueIssues {
          critical
          serious
          moderate
          minor
          total
        }
        categoryIssueCounts {
          accessibility {
            critical
            serious
            moderate
            minor
            total
          }
          eaa {
            critical
            serious
            moderate
            minor
            total
          }
          seo {
            critical
            serious
            moderate
            minor
            total
          }
          geo {
            critical
            serious
            moderate
            minor
            total
          }
          brokenLinks {
            critical
            serious
            moderate
            minor
            total
          }
          toxicLinks {
            critical
            serious
            moderate
            minor
            total
          }
        }
        uniqueCategoryIssueCounts {
          accessibility {
            critical
            serious
            moderate
            minor
            total
          }
          eaa {
            critical
            serious
            moderate
            minor
            total
          }
          seo {
            critical
            serious
            moderate
            minor
            total
          }
          geo {
            critical
            serious
            moderate
            minor
            total
          }
          brokenLinks {
            critical
            serious
            moderate
            minor
            total
          }
          toxicLinks {
            critical
            serious
            moderate
            minor
            total
          }
        }
      }
      startedAt
      completedAt
      trashedAt
      pdfReportUrl
      error
      reviewedLinks
    }
  }
`;

export const GET_PDF_DOWNLOAD_URL = /* GraphQL */ `
  query GetPdfDownloadUrl($siteId: ID!, $scanId: ID!) {
    getPdfDownloadUrl(siteId: $siteId, scanId: $scanId) {
      downloadUrl
      filename
      expiresAt
    }
  }
`;

/**
 * Summary-only page results — no heavy issue arrays.
 * fields: [] strips all heavy fields (accessibilityIssues, eaaIssues, seoResult, geoResult, brokenLinks, toxicLinks).
 * A 444-page summary is ~50KB — well under AppSync's 6MB limit.
 */
export const LIST_PAGE_RESULTS_SUMMARY = /* GraphQL */ `
  query ListPageResultsSummary(
    $scanId: ID!
    $limit: Int
    $nextToken: String
    $sortByScore: Boolean
  ) {
    listPageResults(
      scanId: $scanId
      limit: $limit
      nextToken: $nextToken
      sortByScore: $sortByScore
      fields: []
    ) {
      items {
        scanId
        pageUrl
        score
        grade
        issueCount
        issues {
          critical
          serious
          moderate
          minor
          total
        }
        lighthouseScores {
          performance
          accessibility
          bestPractices
          seo
        }
        coreWebVitals {
          lcp
          fid
          cls
          ttfb
          lcpRating
          fidRating
          clsRating
          ttfbRating
        }
        scannedAt
      }
      nextToken
    }
  }
`;

// Per-tab lazy-load queries — one heavy field per request, paginated at 50 pages each.
// Each batch is ~600KB max — well under AppSync's 6MB limit.
export const LIST_PAGE_RESULTS_ACCESSIBILITY = /* GraphQL */ `
  query ListPageResultsAccessibility($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(
      scanId: $scanId
      limit: $limit
      nextToken: $nextToken
      fields: ["accessibilityIssues"]
    ) {
      items {
        scanId
        pageUrl
        accessibilityIssues {
          ruleId
          wcagCriteria
          wcagLevel
          impact
          description
          helpUrl
          nodes {
            html
            target
            failureSummary
          }
          isWcag22
          estimatedTimeToFix
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS_EAA = /* GraphQL */ `
  query ListPageResultsEaa($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(scanId: $scanId, limit: $limit, nextToken: $nextToken, fields: ["eaaIssues"]) {
      items {
        scanId
        pageUrl
        eaaIssues {
          clauseNumber
          requirement
          severity
          description
          remediation
          annexReference
          pageUrl
          element
          wcagMapping
          toolSource
          ruleId
          category
          viewport
          affectedElements {
            selector
            html
            pageUrl
            failureSummary
          }
          affectedElementCount
          remediationGuidance {
            whyItMatters
            howToFix
            affectedUsers
            codeExample {
              before
              after
            }
            estimatedEffort
            priority
          }
          helpUrl
          needsManualReview
          confidence
          estimatedTimeToFix
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS_SEO = /* GraphQL */ `
  query ListPageResultsSeo($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(scanId: $scanId, limit: $limit, nextToken: $nextToken, fields: ["seoResult"]) {
      items {
        scanId
        pageUrl
        seoResult {
          pageUrl
          overallScore
          categoryScores {
            category
            score
            issueCount
            criticalCount
            warningCount
            infoCount
          }
          issues {
            id
            category
            severity
            type
            title
            description
            impact
            recommendation
            element
            googleDocsUrl
          }
          scannedAt
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS_GEO = /* GraphQL */ `
  query ListPageResultsGeo($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(scanId: $scanId, limit: $limit, nextToken: $nextToken, fields: ["geoResult"]) {
      items {
        scanId
        pageUrl
        geoResult {
          pageUrl
          overallScore
          categoryScores {
            category
            score
            issueCount
            criticalCount
            warningCount
            infoCount
          }
          issues {
            id
            category
            severity
            type
            title
            description
            impact
            recommendation
            element
            referenceUrl
          }
          detectedSchemas
          scannedAt
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS_BROKEN_LINKS = /* GraphQL */ `
  query ListPageResultsBrokenLinks($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(
      scanId: $scanId
      limit: $limit
      nextToken: $nextToken
      fields: ["brokenLinks"]
    ) {
      items {
        scanId
        pageUrl
        brokenLinks {
          sourceUrl
          targetUrl
          linkText
          statusCode
          isExternal
          needsManualReview
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS_TOXIC_LINKS = /* GraphQL */ `
  query ListPageResultsToxicLinks($scanId: ID!, $limit: Int, $nextToken: String) {
    listPageResults(scanId: $scanId, limit: $limit, nextToken: $nextToken, fields: ["toxicLinks"]) {
      items {
        scanId
        pageUrl
        toxicLinks {
          sourceUrl
          targetUrl
          targetDomain
          linkText
          toxicityScore
          flags
          statusCode
          redirectChain
          needsManualReview
        }
      }
      nextToken
    }
  }
`;

export const LIST_PAGE_RESULTS = /* GraphQL */ `
  query ListPageResults($scanId: ID!, $limit: Int, $nextToken: String, $sortByScore: Boolean) {
    listPageResults(
      scanId: $scanId
      limit: $limit
      nextToken: $nextToken
      sortByScore: $sortByScore
    ) {
      items {
        scanId
        pageUrl
        score
        grade
        issueCount
        issues {
          critical
          serious
          moderate
          minor
          total
        }
        accessibilityIssues {
          ruleId
          wcagCriteria
          wcagLevel
          impact
          description
          helpUrl
          nodes {
            html
            target
            failureSummary
          }
          isWcag22
          estimatedTimeToFix
        }
        eaaIssues {
          clauseNumber
          requirement
          severity
          description
          remediation
          annexReference
          pageUrl
          element
          wcagMapping
          toolSource
          ruleId
          category
          viewport
          affectedElements {
            selector
            html
            pageUrl
            failureSummary
          }
          affectedElementCount
          remediationGuidance {
            whyItMatters
            howToFix
            affectedUsers
            codeExample {
              before
              after
            }
            estimatedEffort
            priority
          }
          helpUrl
          needsManualReview
          confidence
          estimatedTimeToFix
        }
        brokenLinks {
          sourceUrl
          targetUrl
          linkText
          statusCode
          isExternal
          needsManualReview
        }
        toxicLinks {
          sourceUrl
          targetUrl
          targetDomain
          linkText
          toxicityScore
          flags
          statusCode
          redirectChain
          needsManualReview
        }
        seoResult {
          pageUrl
          overallScore
          categoryScores {
            category
            score
            issueCount
            criticalCount
            warningCount
            infoCount
          }
          issues {
            id
            category
            severity
            type
            title
            description
            impact
            recommendation
            element
            googleDocsUrl
          }
          scannedAt
        }
        geoResult {
          pageUrl
          overallScore
          categoryScores {
            category
            score
            issueCount
            criticalCount
            warningCount
            infoCount
          }
          issues {
            id
            category
            severity
            type
            title
            description
            impact
            recommendation
            element
            referenceUrl
          }
          detectedSchemas
          scannedAt
        }
        lighthouseScores {
          performance
          accessibility
          bestPractices
          seo
        }
        coreWebVitals {
          lcp
          fid
          cls
          ttfb
          lcpRating
          fidRating
          clsRating
          ttfbRating
        }
        scannedAt
      }
      nextToken
    }
  }
`;

export const GET_PAGE_RESULT = /* GraphQL */ `
  query GetPageResult($scanId: ID!, $pageUrl: String!) {
    getPageResult(scanId: $scanId, pageUrl: $pageUrl) {
      scanId
      pageUrl
      score
      grade
      issueCount
      issues {
        critical
        serious
        moderate
        minor
        total
      }
      accessibilityIssues {
        ruleId
        wcagCriteria
        wcagLevel
        impact
        description
        helpUrl
        nodes {
          html
          target
          failureSummary
        }
        isWcag22
        estimatedTimeToFix
      }
      eaaIssues {
        clauseNumber
        requirement
        severity
        description
        remediation
        annexReference
        estimatedTimeToFix
      }
      brokenLinks {
        sourceUrl
        targetUrl
        linkText
        statusCode
        isExternal
        needsManualReview
      }
      toxicLinks {
        sourceUrl
        targetUrl
        targetDomain
        linkText
        toxicityScore
        flags
        statusCode
        redirectChain
        needsManualReview
      }
      seoResult {
        pageUrl
        overallScore
        categoryScores {
          category
          score
          issueCount
          criticalCount
          warningCount
          infoCount
        }
        issues {
          id
          category
          severity
          type
          title
          description
          impact
          recommendation
          element
          googleDocsUrl
        }
        scannedAt
      }
      geoResult {
        pageUrl
        overallScore
        categoryScores {
          category
          score
          issueCount
          criticalCount
          warningCount
          infoCount
        }
        issues {
          id
          category
          severity
          type
          title
          description
          impact
          recommendation
          element
          referenceUrl
        }
        detectedSchemas
        scannedAt
      }
      lighthouseScores {
        performance
        accessibility
        bestPractices
        seo
      }
      coreWebVitals {
        lcp
        fid
        cls
        ttfb
        lcpRating
        fidRating
        clsRating
        ttfbRating
      }
      scannedAt
    }
  }
`;

export const LIST_WORST_PAGES = /* GraphQL */ `
  query ListWorstPages($scanId: ID!, $limit: Int) {
    listWorstPages(scanId: $scanId, limit: $limit) {
      items {
        scanId
        pageUrl
        score
        grade
        issueCount
        issues {
          critical
          serious
          moderate
          minor
          total
        }
      }
    }
  }
`;

// User queries
export const GET_CURRENT_USER = /* GraphQL */ `
  query GetCurrentUser {
    getCurrentUser {
      userId
      email
      role
      defaultTeamId
      createdAt
      updatedAt
    }
  }
`;

export const LIST_USERS = /* GraphQL */ `
  query ListUsers($limit: Int, $nextToken: String) {
    listUsers(limit: $limit, nextToken: $nextToken) {
      items {
        userId
        email
        role
        defaultTeamId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Team queries
export const GET_TEAM = /* GraphQL */ `
  query GetTeam($teamId: ID!) {
    getTeam(teamId: $teamId) {
      teamId
      name
      ownerId
      createdAt
      updatedAt
      members {
        teamId
        userId
        role
        email
        joinedAt
        invitedBy
      }
    }
  }
`;

export const LIST_TEAMS = /* GraphQL */ `
  query ListTeams($limit: Int, $nextToken: String) {
    listTeams(limit: $limit, nextToken: $nextToken) {
      items {
        teamId
        name
        ownerId
        createdAt
        updatedAt
        members {
          teamId
          userId
          role
          email
          joinedAt
        }
      }
      nextToken
    }
  }
`;

export const GET_MY_TEAMS = /* GraphQL */ `
  query GetMyTeams {
    getMyTeams {
      items {
        teamId
        name
        ownerId
        createdAt
        updatedAt
        members {
          userId
          role
          email
        }
      }
    }
  }
`;

// Schedule queries
export const GET_SCHEDULE = /* GraphQL */ `
  query GetSchedule($scheduleId: ID!) {
    getSchedule(scheduleId: $scheduleId) {
      scheduleId
      siteId
      userId
      frequency
      nextRunAt
      dayOfWeek
      dayOfMonth
      hour
      minute
      timezone
      enabled
      lastRunAt
      config {
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      createdAt
      updatedAt
    }
  }
`;

export const LIST_SCHEDULES = /* GraphQL */ `
  query ListSchedules($siteId: ID!) {
    listSchedules(siteId: $siteId) {
      items {
        scheduleId
        siteId
        userId
        frequency
        nextRunAt
        dayOfWeek
        dayOfMonth
        hour
        minute
        timezone
        enabled
        lastRunAt
        config {
          scanTypes
          wcagVersion
          wcagLevel
          pageLimit
          rateLimit
          selectedTools
          pauseAfterCrawl
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// API Key queries
export const LIST_API_KEYS = /* GraphQL */ `
  query ListApiKeys($teamId: ID!) {
    listApiKeys(teamId: $teamId) {
      name
      keyType
      createdAt
      lastUsedAt
    }
  }
`;

// Issue queries
export const LIST_ISSUES = /* GraphQL */ `
  query ListIssues(
    $scanId: ID!
    $limit: Int
    $nextToken: String
    $filters: IssueFiltersInput
    $sort: IssueSortInput
    $grouped: Boolean
  ) {
    listIssues(
      scanId: $scanId
      limit: $limit
      nextToken: $nextToken
      filters: $filters
      sort: $sort
      grouped: $grouped
    ) {
      items {
        id
        category
        severity
        type
        ruleId
        message
        description
        pageUrl
        element {
          html
          selector
          target
        }
        affectedPages
        affectedElements {
          html
          selector
          target
          pageUrl
          failureSummary
        }
        remediation
        referenceUrl
        wcagCriteria
        wcagLevel
        isWcag22
        toolSource
        clauseNumber
        legalRisk
        statusCode
        isExternal
        needsManualReview
      }
      totalCount
      nextToken
    }
  }
`;

// Risk Acceptance queries
export const GET_RISK_ACCEPTANCE = /* GraphQL */ `
  query GetRiskAcceptance($siteId: ID!, $acceptanceId: ID!) {
    getRiskAcceptance(siteId: $siteId, acceptanceId: $acceptanceId) {
      siteId
      acceptanceId
      issueId
      issueDescription
      userId
      acceptedBy
      acceptedAt
      expiresAt
      reason
      status
      resolvedAt
      resolvedBy
      resolutionNote
      pageUrls
      severity
      category
    }
  }
`;

export const LIST_RISK_ACCEPTANCES = /* GraphQL */ `
  query ListRiskAcceptances(
    $siteId: ID!
    $status: AcceptanceStatus
    $limit: Int
    $nextToken: String
  ) {
    listRiskAcceptances(siteId: $siteId, status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        siteId
        acceptanceId
        issueId
        issueDescription
        userId
        acceptedBy
        acceptedAt
        expiresAt
        reason
        status
        resolvedAt
        resolvedBy
        resolutionNote
        pageUrls
        severity
        category
      }
      nextToken
    }
  }
`;

export const LIST_EXPIRING_ACCEPTANCES = /* GraphQL */ `
  query ListExpiringAcceptances($daysUntilExpiry: Int, $limit: Int) {
    listExpiringAcceptances(daysUntilExpiry: $daysUntilExpiry, limit: $limit) {
      siteId
      acceptanceId
      issueId
      issueDescription
      acceptedBy
      acceptedAt
      expiresAt
      reason
      status
      severity
      category
    }
  }
`;

export const CHECK_ISSUE_ACCEPTANCE = /* GraphQL */ `
  query CheckIssueAcceptance($siteId: ID!, $issueId: String!) {
    checkIssueAcceptance(siteId: $siteId, issueId: $issueId) {
      siteId
      acceptanceId
      issueId
      issueDescription
      acceptedBy
      acceptedAt
      expiresAt
      reason
      status
    }
  }
`;

// Time Estimate queries
export const GET_SCAN_TIME_ESTIMATES = /* GraphQL */ `
  query GetScanTimeEstimates($scanId: ID!) {
    getScanTimeEstimates(scanId: $scanId) {
      totalMinutes
      issuesWithEstimates
      totalIssues
      bySeverity {
        critical
        serious
        moderate
        minor
      }
    }
  }
`;

// Google Search Console queries
export const GET_GSC_CONNECTION = /* GraphQL */ `
  query GetGscConnection($siteId: ID!) {
    getGscConnection(siteId: $siteId) {
      siteId
      gscPropertyUrl
      connectedAt
      connectedBy
      lastSyncAt
      status
    }
  }
`;

export const LIST_TOXIC_BACKLINKS = /* GraphQL */ `
  query ListToxicBacklinks($scanId: ID!, $limit: Int, $nextToken: String) {
    listToxicBacklinks(scanId: $scanId, limit: $limit, nextToken: $nextToken) {
      items {
        linkingUrl
        linkingDomain
        targetUrl
        anchorText
        toxicityScore
        flags
        statusCode
        redirectChain
        analyzedAt
      }
      summary {
        total
        highRisk
        mediumRisk
        lowRisk
        uniqueDomains
      }
      nextToken
    }
  }
`;
